import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import {
  UseFieldArrayReturn,
  UseFormReturn,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import useSWR, { mutate } from 'swr';

import { useAllEnums } from '~/data/enums';
import { useSelectedEmployeeId } from '~/hooks/employee';

import {
  EquipListType,
  Equipment,
  EquipmentType,
  getCompanyEquipment,
  getCompanyEquipmentList,
  getEquipNum,
  prefetchEquipmentData,
} from './data';
import { mapDefaultData } from './data/dataMapping';
import { dataSaving } from './data/dataSaving';

export type FixForKeyIssue = {
  [key: string]: number | string | null | boolean | File | undefined;
};
export interface RowData extends FixForKeyIssue {
  bnNewCompanyItem?: boolean;
  fEquipmentAddUrl?: string;
  fEquipmentId?: number;
  fEquipmentCompEqFK?: number;
  fEquipmentType: string;
  fEquipmentItem?: string;
  fEquipmentInvNumber: string;
  fEquipmentDeliveryDate: string | null;
  fEquipmentAttachmentId: number;
  fEquipmentAttachment: File | undefined;
  fEquipmentAttachmentName: string;
  fEquipmentReturnDate?: string | null;
  fEquipmentInfo?: string;
  fEquipmentExpirationDate?: string | null;
  fComEquipmentId?: number;
}

export type IFormInput = {
  equipment: RowData[];
};

export type UseEquipmentTabOutput = {
  companyEquip: EquipmentType[];
  equipEnumType: Equipment[];
  onSubmit: () => void;
  equipList: EquipListType[];
  useFieldArrayMethods: UseFieldArrayReturn<IFormInput, 'equipment', 'id'>;
  useFormMethods: UseFormReturn<IFormInput>;
  updateInvNumber: (num: string) => void;
  invNumber: string;
  defaultData: RowData[];
  setEditedRows: Dispatch<SetStateAction<number[]>>;
  setDeletedRows: Dispatch<SetStateAction<number[]>>;
  editMode: boolean;
  setEditMode: Dispatch<SetStateAction<boolean>>;
  isLoading: boolean;
};

export const useEquipmentTab = (): UseEquipmentTabOutput => {
  const useFormMethods = useForm<IFormInput>();
  const { handleSubmit, control, reset } = useFormMethods;
  const useFieldArrayMethods = useFieldArray({ name: 'equipment', control });
  const { selectedEmployeeId } = useSelectedEmployeeId();
  const empId = String(selectedEmployeeId);
  const { allEnums } = useAllEnums();
  const equipEnumType = allEnums['EQUIPMENT_TYPE'];
  const { data: equipList, isLoading: equipListLoading } = useSWR(
    'equipList',
    () => getCompanyEquipmentList(),
  );
  const { data: invNumber } = useSWR('equipNum', () => getEquipNum());
  const updateInvNumber = (num?: string) =>
    mutate('equipNum', getEquipNum(num));

  const { data: companyEquip, mutate: mutateCompanyEquip } = useSWR(
    ['equipmentData', empId],
    () =>
      getCompanyEquipment(empId).then((data) =>
        data.sort((a: EquipmentType, b: EquipmentType) => {
          return (
            new Date(b.fEquipmentDeliveryDate).valueOf() -
            new Date(a.fEquipmentDeliveryDate).valueOf()
          );
        }),
      ),
  );

  const [defaultData, setDefaultData] = useState<RowData[]>([]);
  const [editedRows, setEditedRows] = useState<number[]>([]);
  const [deletedRows, setDeletedRows] = useState<number[]>([]);
  const [editMode, setEditMode] = useState<boolean>(false);

  useEffect(() => {
    if (companyEquip) {
      const newDefaultData = mapDefaultData(companyEquip || []);
      setDefaultData(newDefaultData);
      reset({
        equipment: newDefaultData,
      });
    }
  }, [companyEquip, empId, reset]);

  const handleFormSubmit = useMemo(() => {
    return handleSubmit((data) => {
      const rowData = data.equipment;
      (async () => {
        await dataSaving(
          parseInt(empId),
          deletedRows,
          editedRows,
          defaultData,
          rowData,
          equipList!,
        );
        await mutateCompanyEquip();
        prefetchEquipmentData();
        setEditMode(!editMode);
      })();
    });
  }, [
    handleSubmit,
    empId,
    deletedRows,
    editedRows,
    defaultData,
    equipList,
    mutateCompanyEquip,
    editMode,
  ]);

  return {
    defaultData,
    useFormMethods,
    companyEquip: companyEquip || [],
    equipEnumType,
    equipList: equipList || [],
    onSubmit: handleFormSubmit,
    useFieldArrayMethods,
    updateInvNumber,
    invNumber: invNumber as string,
    setEditedRows,
    setDeletedRows,
    editMode,
    setEditMode,
    isLoading: equipListLoading,
  };
};
